import { z } from 'zod';

/**
 * Quem mandou a mensagem — o eixo `autor`, pedido do dono em 2026-09-21
 * ("mostrar nome do usuário que enviou a mensagem no remoto").
 *
 * **Duas decisões do dono moram neste arquivo, e nenhuma delas se reabre aqui.**
 *
 * 1. **Só mensagem NOVA leva nome.** Nada é reconstruído para trás. A medição
 *    contra a produção tentou casar `atividades_usuario` com `eventos_sessao`
 *    por (sessão + texto idêntico): 21 de 24 casaram, e os três que faltaram
 *    falharam porque `remoto_texto_do_evento` devolve string vazia para
 *    aquela forma de carga — quer dizer, o evento existe e o casamento não o
 *    alcança. Casar por texto é frágil, e por isso NÃO É O CAMINHO: o autor
 *    viaja com a mensagem, carimbado por quem a mandou.
 * 2. **Sem pessoa, marca-se a ORIGEM.** Despacho da mestre aparece como vindo
 *    da mestre. Conversa aberta no editor (VS Code, que nunca passou pelo
 *    painel) não tem autor nenhum aqui e o painel a rotula pelo que sabe —
 *    a sessão é OBSERVADA —, sem inventar campo no fio para isso.
 *
 * **O nome viaja como TEXTO, e é de propósito.** Um id de usuário morreria com
 * a conta: a trilha desta casa é sem chave estrangeira justamente para o
 * histórico não sumir com a pessoa (`atividades_usuario`), e o mesmo vale aqui.
 * O que a conversa guarda é como a pessoa se chamava quando escreveu.
 */
export const TipoDeAutorSchema = z.enum(['PESSOA', 'MESTRE']);
export type TipoDeAutor = z.infer<typeof TipoDeAutorSchema>;

/** Teto do nome exibido. Corta no servidor, nunca na tela. */
export const NOME_DO_AUTOR_MAXIMO = 120;

export const AutorDaMensagemSchema = z.object({
  tipo: TipoDeAutorSchema,
  /** Como aparece na bolha. Já cortado por quem o montou. */
  nome: z.string().min(1).max(NOME_DO_AUTOR_MAXIMO),
});
export type AutorDaMensagem = z.infer<typeof AutorDaMensagemSchema>;

/**
 * A MARCA que liga uma mensagem enviada ao evento que ela vira.
 *
 * Os três caminhos de envio têm, cada um, um campo de cliente que o motor
 * devolve intacto — **medido em 2026-09-21, nos três**:
 *
 * | caminho | onde entra | onde volta |
 * |---|---|---|
 * | sessão do painel (Claude) | `uuid` da `SDKUserMessage` | `uuid` da linha do transcript |
 * | sessão observada (canal local) | `uuid` do quadro de peer | `uuid` da linha do transcript |
 * | sessão do painel (Codex) | `clientUserMessageId` do `turn/start` | `clientId` do `userMessage` |
 *
 * Nos dois primeiros a marca JÁ É o `uuidTurno` do evento. No Codex não: lá o
 * `uuidTurno` é o `item.id` que o app-server sorteia, e a marca volta ao lado
 * dele — por isso o daemon reaponta a etiqueta quando o item chega.
 */
export function ehMarcaDeAutor(valor: unknown): valor is string {
  return typeof valor === 'string' && valor.length > 0 && valor.length <= 200;
}
